/**
 * One change to the ground: a crater of radius r at (x, y), or, with `fill` set, a w×h block of
 * new ground centred there (a thrown platform, a sandbag wall). Hosts send these to replicas.
 */
export interface Carve {
  x: number;
  y: number;
  r: number;
  w?: number;
  h?: number;
  fill?: BuildFill;
}

export type BuildFill = 'plank' | 'bags';

/** How built ground is painted into the fill and cap pictures. */
export const BUILD_COLORS: Record<BuildFill, { body: string; cap: string }> = {
  plank: { body: '#c98c4e', cap: '#e8b872' },
  bags: { body: '#b5a070', cap: '#d6c48f' },
};

/**
 * Collision mask for a level. `solid` is the truth the simulation reads; a canvas can be
 * attached so optional destruction is reflected in the picture too.
 */
function hexRgb(hex: string): [number, number, number] {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

export class Terrain {
  readonly w: number;
  readonly h: number;
  readonly solid: Uint8Array;
  canvas: HTMLCanvasElement | null = null;
  /** The cap-colour picture, so built ground gets a lighter top like everything else. */
  capCanvas: HTMLCanvasElement | null = null;
  /**
   * Picture changes the renderer has not dressed yet: craters, built blocks, or `r < 0` when the
   * whole mask was replaced. Drained by whoever draws the terrain.
   */
  readonly dirty: Carve[] = [];

  constructor(w: number, h: number) {
    this.w = w;
    this.h = h;
    this.solid = new Uint8Array(w * h);
  }

  isSolid(x: number, y: number): boolean {
    const xi = x | 0;
    const yi = y | 0;
    if (xi < 0 || xi >= this.w || yi < 0 || yi >= this.h) return false;
    return this.solid[yi * this.w + xi] === 1;
  }

  /** First solid pixel scanning down from `fromY`; `h` if the column is empty below it. */
  surfaceY(x: number, fromY = 0): number {
    const xi = Math.max(0, Math.min(this.w - 1, x | 0));
    for (let y = Math.max(0, fromY | 0); y < this.h; y++) {
      if (this.solid[y * this.w + xi] === 1) return y;
    }
    return this.h;
  }

  fillRect(x: number, y: number, w: number, h: number): void {
    const x0 = Math.max(0, Math.floor(x));
    const x1 = Math.min(this.w, Math.ceil(x + w));
    const y0 = Math.max(0, Math.floor(y));
    const y1 = Math.min(this.h, Math.ceil(y + h));
    for (let yy = y0; yy < y1; yy++) this.solid.fill(1, yy * this.w + x0, yy * this.w + x1);
  }

  fillCircle(cx: number, cy: number, r: number): void {
    this.paintCircle(cx, cy, r, 1);
  }

  /** Scanline fill of a simple polygon. */
  fillPolygon(pts: readonly (readonly [number, number])[]): void {
    if (pts.length < 3) return;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const [, y] of pts) {
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
    const y0 = Math.max(0, Math.floor(minY));
    const y1 = Math.min(this.h - 1, Math.ceil(maxY));
    const xs: number[] = [];
    for (let y = y0; y <= y1; y++) {
      xs.length = 0;
      const sy = y + 0.5;
      for (let i = 0; i < pts.length; i++) {
        const [ax, ay] = pts[i]!;
        const [bx, by] = pts[(i + 1) % pts.length]!;
        if (ay === by) continue;
        if ((sy >= ay && sy < by) || (sy >= by && sy < ay)) {
          xs.push(ax + ((sy - ay) * (bx - ax)) / (by - ay));
        }
      }
      xs.sort((a, b) => a - b);
      for (let i = 0; i + 1 < xs.length; i += 2) {
        const xa = Math.max(0, Math.round(xs[i]!));
        const xb = Math.min(this.w, Math.round(xs[i + 1]!));
        if (xb > xa) this.solid.fill(1, y * this.w + xa, y * this.w + xb);
      }
    }
  }

  /**
   * Replaces the whole mask with one received from the host (a late join or re-join). If a canvas is
   * attached, pixels that are air now get punched out of it so the picture matches the host's.
   */
  setMask(mask: Uint8Array): void {
    if (mask.length !== this.solid.length) throw new Error(`terrain mask has ${mask.length} entries, expected ${this.solid.length}`);
    this.solid.set(mask);
    this.applyMaskToCanvas();
    this.dirty.push({ x: 0, y: 0, r: -1 });
  }

  /**
   * Make the attached pictures match the mask (a picture attached late, or a replaced mask): air
   * pixels are punched out, and solid pixels the picture lacks (planks built before we had a
   * picture) are painted in plank colours.
   */
  applyMaskToCanvas(): void {
    const ctx = this.canvas?.getContext('2d');
    if (!ctx) return;
    const img = ctx.getImageData(0, 0, this.w, this.h);
    const capCtx = this.capCanvas?.getContext('2d') ?? null;
    const cap = capCtx ? capCtx.getImageData(0, 0, this.w, this.h) : null;
    const body = hexRgb(BUILD_COLORS.plank.body);
    const top = hexRgb(BUILD_COLORS.plank.cap);
    let changed = false;
    for (let i = 0; i < this.solid.length; i++) {
      const o = i * 4;
      if (this.solid[i] === 0) {
        if (img.data[o + 3] === 0) continue;
        img.data[o + 3] = 0;
        if (cap) cap.data[o + 3] = 0;
        changed = true;
      } else if (img.data[o + 3] === 0) {
        img.data[o] = body[0];
        img.data[o + 1] = body[1];
        img.data[o + 2] = body[2];
        img.data[o + 3] = 255;
        if (cap) {
          cap.data[o] = top[0];
          cap.data[o + 1] = top[1];
          cap.data[o + 2] = top[2];
          cap.data[o + 3] = 255;
        }
        changed = true;
      }
    }
    if (!changed) return;
    ctx.putImageData(img, 0, 0);
    if (capCtx && cap) capCtx.putImageData(cap, 0, 0);
  }

  /** Apply one change from the host: a crater or a built block. */
  apply(op: Carve): void {
    if (op.fill && op.w && op.h) this.build(op.x, op.y, op.w, op.h, op.fill);
    else this.carve(op.x, op.y, op.r);
  }

  /** New ground: a w×h block centred on (cx, cy), solid at once and painted into the pictures. */
  build(cx: number, cy: number, w: number, h: number, fill: BuildFill): Carve {
    const x = cx - w / 2;
    const y = cy - h / 2;
    this.fillRect(x, y, w, h);
    const colors = BUILD_COLORS[fill];
    const paint = (canvas: HTMLCanvasElement | null, color: string): void => {
      const ctx = canvas?.getContext('2d');
      if (!ctx) return;
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = color;
      ctx.fillRect(Math.floor(x), Math.floor(y), Math.ceil(w), Math.ceil(h));
      if (fill === 'plank') {
        // Board seams so a plank reads as planks.
        ctx.fillStyle = 'rgba(42,26,18,0.35)';
        for (let sx = Math.floor(x) + 24; sx < x + w - 4; sx += 26) ctx.fillRect(sx, Math.floor(y), 2, Math.ceil(h));
      } else {
        ctx.fillStyle = 'rgba(42,26,18,0.3)';
        for (let sy = Math.floor(y) + 14; sy < y + h - 2; sy += 15) ctx.fillRect(Math.floor(x), sy, Math.ceil(w), 2);
      }
      ctx.restore();
    };
    paint(this.canvas, colors.body);
    paint(this.capCanvas, colors.cap);
    const op: Carve = { x: cx, y: cy, r: 0, w, h, fill };
    this.dirty.push(op);
    return op;
  }

  carve(cx: number, cy: number, r: number): void {
    this.paintCircle(cx, cy, r, 0);
    const ctx = this.canvas?.getContext('2d');
    if (ctx) {
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    this.dirty.push({ x: cx, y: cy, r });
  }

  private paintCircle(cx: number, cy: number, r: number, value: 0 | 1): void {
    const r2 = r * r;
    const x0 = Math.max(0, Math.floor(cx - r));
    const x1 = Math.min(this.w - 1, Math.ceil(cx + r));
    const y0 = Math.max(0, Math.floor(cy - r));
    const y1 = Math.min(this.h - 1, Math.ceil(cy + r));
    for (let y = y0; y <= y1; y++) {
      const dy = y - cy;
      const row = y * this.w;
      for (let x = x0; x <= x1; x++) {
        const dx = x - cx;
        if (dx * dx + dy * dy <= r2) this.solid[row + x] = value;
      }
    }
  }
}
